import {
  Box,
  Button,
  Container,
  Flex,
  GridItem,
  Heading,
  Image,
  SimpleGrid,
  Text,
} from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import { motion } from "framer-motion"

import checkRed from "#/assets/check-red-simple.svg"
import { LANDING_CONTAINER_WIDTH, routes } from "#/shared/constants"

type FeatureItem = {
  title: string
  paragraph: string
  image: string
}

type LandingFeatureProps = {
  feature?: FeatureItem[]
  featureHeader?: string
}

export default function HomeFeatures({
  feature = [],
  featureHeader = "",
}: LandingFeatureProps) {
  return (
    <motion.div
      initial="hidden"
      transition={{ type: "spring", stiffness: 100, damping: 15 }}
      whileInView="visible"
      viewport={{ once: true, amount: 0.0 }}
      variants={{
        hidden: { y: -100 },
        visible: { y: -200 },
      }}
    >
      <Flex
        alignContent="flex-start"
        position="relative"
        background="#F4F0ED"
        mt="-20px"
      >
        <Container
          maxWidth={LANDING_CONTAINER_WIDTH}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          background="#F4F0ED"
        >
          <Text
            as="h2"
            fontSize={{ base: "26px", md: "44px" }}
            lineHeight="100%"
            fontFamily="riforma.regular"
            textAlign="center"
            color="primary.red.100"
            letterSpacing={{ base: "-1px", md: "-2px" }}
            mt="60px"
            mb="20px"
            maxWidth={{ base: "100%", md: "100%" }}
          >
            {featureHeader} <br />
          </Text>
          <SimpleGrid
            columns={{ base: 2, md: 5 }}
            spacing={{ base: 5, md: 0 }}
            mt="10px"
          >
            {[
              "Permissionless",
              "Bank-free",
              "Intuitive",
              "Secure",
              "Decentralized",
            ].map((label, index, array) => (
              <GridItem
                key={label}
                colSpan={
                  index === array.length - 1 && array.length % 2 !== 0
                    ? { base: 2, md: 1 }
                    : 1
                }
                display="flex"
                justifyContent="center"
              >
                <Text
                  fontSize={{ base: "12px", md: "18px" }}
                  mb={{ base: "0px", md: "40px" }}
                  color="primary.black.100"
                  textTransform="uppercase"
                  textAlign="center"
                  fontFamily="riforma.monoRegular"
                >
                  <Flex gap={3} justify="center" align="center">
                    <Image src={checkRed} />
                    {label}
                  </Flex>
                </Text>
              </GridItem>
            ))}
          </SimpleGrid>
          <Link to={routes.EXPLORE}>
            <Button
              variant="primary"
              className="steps-signup"
              bg="black"
              color="white"
              p="10px 40px"
              m="auto"
              h={{ base: "0", md: "60px" }}
              fontSize="20px"
              borderRadius="25px"
              fontFamily="riforma.medium"
              fontWeight="500"
              visibility={{ base: "hidden", md: "visible" }}
              _hover={{
                color: "white",
                background: "primary.red.100",
              }}
            >
              Explore Mezo
            </Button>
          </Link>
        </Container>
      </Flex>
      <Container
        maxWidth="100%"
        display="flex"
        justifyContent="space-between"
        flexDirection="column"
        background="#F4F0ED"
        mb="0"
      >
        <Box
          maxWidth={{ base: "100%", md: "100%" }}
          padding={{ base: 0, md: 10 }}
          margin="auto"
          height="auto"
          borderTopRadius="40px"
        >
          {feature.length > 0 && (
            <Flex
              justifyContent="center"
              flexDirection={{ base: "column", md: "column" }}
            >
              {feature.map((item, index) => (
                <motion.div
                  initial="hidden"
                  transition={{ type: "spring", stiffness: 100, damping: 15 }}
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.0 }}
                  variants={{
                    hidden: { y: 120, opacity: 0 },
                    visible: { y: 0, opacity: 1 },
                  }}
                >
                  <Box
                    key={item.title}
                    border="2px solid #F4F0ED"
                    p={{ base: "20px", md: "32px" }}
                    pb={{ base: "32px", md: "0px" }}
                    m={{ base: "20px 0", md: 6 }}
                    borderRadius="32px"
                    w={{ base: "100%", md: "100%" }}
                  >
                    <Flex
                      gap={10}
                      justify={index === 3 ? "center" : "flex-start"}
                      alignItems={index === 3 ? "flex-start" : "center"}
                      direction={(() => {
                        if (index === 1)
                          return { base: "column", md: "row-reverse" }
                        if (index === 2)
                          return { base: "column", md: "column-reverse" }
                        return { base: "column", md: "row" }
                      })()}
                    >
                      <Image
                        src={item.image}
                        maxWidth={{
                          base: "100%",
                          md: index === 2 ? "80%" : "60%",
                        }}
                      />
                      <Box
                        flex="1"
                        textAlign={{
                          base: "left",
                          md: index === 2 ? "center" : "left",
                        }}
                        p={{ base: 0, md: "40px" }}
                      >
                        <Heading
                          fontFamily="riforma.thin"
                          as="h3"
                          fontWeight="200"
                          fontSize={{ base: "26px", md: "42px" }}
                          letterSpacing="-2px"
                          lineHeight="120%"
                          mb="20px"
                          color="primary.red.100"
                        >
                          {item.title}
                        </Heading>
                        <Text fontSize={{ base: "14px", md: "16px" }}>
                          {item.paragraph}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </motion.div>
              ))}
            </Flex>
          )}
          <Flex
            maxWidth="1280px"
            margin="auto"
            width="100%"
            textAlign="center"
            alignItems="center"
            justifyContent="center"
          />
        </Box>
      </Container>
    </motion.div>
  )
}
